import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { ArrowBack, Visibility, VisibilityOff } from "@mui/icons-material";
import AppLogo from "../components/AppLogo";
import ThemeToggle from "../components/ThemeToggle";
import Popup from "../components/Popup";
import { APP_NAME } from "../config/brand";
import { confirmPasswordReset, validatePasswordResetToken } from "../utils/passwordResetApi";
import { MIN_PASSWORD_LENGTH } from "../utils/passwordValidation";

const ResetPasswordPage = ({ loginPath = "/login" }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";

    const [checking, setChecking] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [emailHint, setEmailHint] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [toggle, setToggle] = useState(false);
    const [toggleConfirm, setToggleConfirm] = useState(false);
    const [loader, setLoader] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!token) {
                setChecking(false);
                setTokenValid(false);
                return;
            }
            try {
                const data = await validatePasswordResetToken(token);
                if (cancelled) return;
                setTokenValid(Boolean(data?.valid));
                setEmailHint(data?.emailHint || "");
                if (!data?.valid) {
                    setMessage(data?.message || "This reset link is invalid or has expired.");
                    setShowPopup(true);
                }
            } catch (err) {
                if (cancelled) return;
                setTokenValid(false);
                setMessage(err?.response?.data?.message || err?.message || "Could not validate reset link.");
                setShowPopup(true);
            } finally {
                if (!cancelled) setChecking(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (password.length < MIN_PASSWORD_LENGTH) {
            setMessage(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
            setShowPopup(true);
            return;
        }
        if (password !== confirmPassword) {
            setMessage("Passwords do not match.");
            setShowPopup(true);
            return;
        }

        setLoader(true);
        try {
            const data = await confirmPasswordReset({ token, newPassword: password });
            setMessage(data?.message || "Password updated. You can sign in now.");
            setShowPopup(true);
            setTimeout(() => navigate(loginPath, { replace: true }), 1800);
        } catch (err) {
            setMessage(
                err?.response?.data?.message || err?.message || "Could not update password. Try requesting a new link.",
            );
            setShowPopup(true);
        } finally {
            setLoader(false);
        }
    };

    return (
        <>
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "background.default",
                    px: 3,
                    py: 6,
                }}
            >
                <Box sx={{ width: "100%", maxWidth: 420, position: "relative" }}>
                    <Box sx={{ position: "absolute", top: 0, right: 0 }}>
                        <ThemeToggle size="small" />
                    </Box>

                    <Button
                        component={Link}
                        to={loginPath}
                        startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                        sx={{
                            mb: 3,
                            color: "text.secondary",
                            fontFamily: '"DM Sans", sans-serif',
                            fontSize: 13,
                            px: 0,
                            "&:hover": { color: "text.primary", bgcolor: "transparent" },
                        }}
                    >
                        Back to sign in
                    </Button>

                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                        <AppLogo size={32} />
                        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700 }}>
                            {APP_NAME}
                        </Typography>
                    </Stack>

                    <Typography variant="h4" sx={{ mb: 0.75 }}>
                        Choose a new password
                    </Typography>
                    {emailHint && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Resetting access for {emailHint}
                        </Typography>
                    )}

                    {checking ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : tokenValid ? (
                        <Box component="form" noValidate onSubmit={handleSubmit}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label="New password"
                                type={toggle ? "text" : "password"}
                                autoComplete="new-password"
                                autoFocus
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                helperText={`At least ${MIN_PASSWORD_LENGTH} characters`}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setToggle(!toggle)} edge="end">
                                                {toggle ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mt: 0 }}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label="Confirm new password"
                                type={toggleConfirm ? "text" : "password"}
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setToggleConfirm(!toggleConfirm)} edge="end">
                                                {toggleConfirm ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="large"
                                disabled={loader}
                                sx={{ mt: 3, py: 1.4 }}
                            >
                                {loader ? (
                                    <CircularProgress size={24} sx={{ color: "#0a0c0f" }} />
                                ) : (
                                    "Update password"
                                )}
                            </Button>
                        </Box>
                    ) : (
                        <Button component={Link} to="/forgot-password" fullWidth variant="contained">
                            Request a new reset link
                        </Button>
                    )}
                </Box>
            </Box>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </>
    );
};

export default ResetPasswordPage;
